import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth, formatError } from "../lib/auth";
import { ArrowLeft, Sparkles, MessageSquare, Info, Camera, Type } from "lucide-react";

const SOURCES = [
  { id: "whatsapp", label: "WhatsApp" },
  { id: "messenger", label: "Messenger" },
  { id: "sms", label: "SMS" },
  { id: "instagram", label: "Instagram" },
  { id: "tinder", label: "Tinder/Bumble" },
  { id: "other", label: "Jiné" },
];

const RELATIONSHIPS = [
  { id: "dating", label: "Randíme" },
  { id: "partner", label: "Partner/ka" },
  { id: "ex", label: "Bývalý/á" },
  { id: "crush", label: "Sympatie" },
  { id: "friend", label: "Kamarád/ka" },
  { id: "other", label: "Jiné" },
];

const EXAMPLE = `Já: Ahoj, chyběl mi včera večer náš rozhovor 💕
On: Promiň, byl jsem zaneprázdněný
Já: Můžeme si zavolat dneska?
On: Možná, uvidíme jak budu mít čas
Já: Mám pocit, že se nám to v poslední době vzdaluje...
On: Děláš z toho větší věc než to je. Vždyť pořád píšeme.`;

export default function Analyze() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [mode, setMode] = useState("text"); // 'text' | 'image'
  const [text, setText] = useState("");
  const [image, setImage] = useState(null); // {base64, mime, preview}
  const [source, setSource] = useState("whatsapp");
  const [relationship, setRelationship] = useState("dating");
  const [theirName, setTheirName] = useState("");
  const [context, setContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef();

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      setError("Obrázek je příliš velký (max 5 MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImage({ base64: reader.result, mime: f.type, preview: reader.result });
      setError("");
    };
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    setError(""); setLoading(true);
    try {
      let resp;
      if (mode === "image") {
        if (!image) { setError("Vyber screenshot konverzace."); setLoading(false); return; }
        resp = await api.post("/analyze/image", {
          image_base64: image.base64,
          mime_type: image.mime,
          their_name: theirName.trim(),
          context: context.trim(),
          source,
          relationship,
        });
      } else {
        if (text.trim().length < 20) { setError("Konverzace je moc krátká. Vlož alespoň pár zpráv."); setLoading(false); return; }
        resp = await api.post("/analyze", {
          conversation_text: text.trim(),
          their_name: theirName.trim(),
          context: context.trim(),
          source,
          relationship,
        });
      }
      nav(`/analyses/${resp.data.id}`);
    } catch (e) {
      const msg = formatError(e);
      setError(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="app-shell bg-aurora min-h-screen pb-24" data-testid="analyze-page">
      <div className="px-6 pt-10 pb-5 flex items-center gap-3">
        <button onClick={() => nav("/home")} className="text-[#5F7F77]" data-testid="analyze-back"><ArrowLeft size={20} strokeWidth={1.7} /></button>
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-[#5F7F77] font-medium">Sentra AI</p>
          <h1 className="font-display text-3xl text-[#1A2924] leading-tight">Analyzovat konverzaci</h1>
        </div>
      </div>

      <div className="px-6 space-y-5">
        {/* Privacy notice */}
        <div className="rounded-2xl bg-[#FFE4CC] border border-[#DEE7E2] p-3.5 flex items-start gap-2.5">
          <Info size={16} className="text-[#C9A66B] shrink-0 mt-0.5" strokeWidth={1.7} />
          <p className="text-xs text-[#54635F] leading-relaxed">
            <strong className="text-[#1A2924]">Tvoje soukromí je posvátné.</strong> Konverzaci analyzujeme jen pro tebe a uložíme ji bezpečně. Nikdo jiný ji neuvidí.
          </p>
        </div>

        {/* Mode toggle */}
        <div className="grid grid-cols-2 gap-2 p-1 bg-white/70 rounded-2xl border border-[#DEE7E2]" data-testid="mode-toggle">
          <button onClick={() => setMode("image")} data-testid="mode-image"
            className={`py-2.5 rounded-xl text-sm flex items-center justify-center gap-1.5 transition-all ${mode === "image" ? "bg-[#C9A66B] text-white shadow" : "text-[#54635F]"}`}>
            <Camera size={15} /> Nahrát screenshot
          </button>
          <button onClick={() => setMode("text")} data-testid="mode-text"
            className={`py-2.5 rounded-xl text-sm flex items-center justify-center gap-1.5 transition-all ${mode === "text" ? "bg-[#C9A66B] text-white shadow" : "text-[#54635F]"}`}>
            <Type size={15} /> Vložit text
          </button>
        </div>

        {/* Source pills */}
        <div>
          <label className="label-soft">Odkud je konverzace</label>
          <div className="flex flex-wrap gap-2" data-testid="source-picker">
            {SOURCES.map((s) => (
              <button key={s.id} onClick={() => setSource(s.id)} data-testid={`src-${s.id}`}
                className={`px-3.5 py-1.5 rounded-full text-xs transition-all ${source === s.id ? "bg-[#C9A66B] text-white" : "bg-white border border-[#DEE7E2] text-[#1A2924]"}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Relationship */}
        <div>
          <label className="label-soft">Vztah s touto osobou</label>
          <div className="flex flex-wrap gap-2" data-testid="rel-picker">
            {RELATIONSHIPS.map((s) => (
              <button key={s.id} onClick={() => setRelationship(s.id)} data-testid={`rel-${s.id}`}
                className={`px-3.5 py-1.5 rounded-full text-xs transition-all ${relationship === s.id ? "bg-[#C9A66B] text-white" : "bg-white border border-[#DEE7E2] text-[#1A2924]"}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="label-soft">Jméno druhé osoby (volitelné)</label>
          <input className="input-field" value={theirName} onChange={(e) => setTheirName(e.target.value)} placeholder="Např. Martin" data-testid="their-name" />
        </div>

        {mode === "image" ? (
          <div data-testid="image-mode">
            <label className="label-soft">Screenshot konverzace</label>
            {image ? (
              <div className="relative rounded-2xl overflow-hidden border border-[#DEE7E2]">
                <img src={image.preview} alt="" className="w-full max-h-80 object-contain bg-[#FFE4CC]" />
                <button onClick={() => { setImage(null); if(fileRef.current) fileRef.current.value = ""; }} className="absolute top-2 right-2 bg-white/90 backdrop-blur rounded-full px-3 py-1 text-xs text-[#C9A66B]" data-testid="image-remove">
                  Změnit
                </button>
              </div>
            ) : (
              <button onClick={() => fileRef.current?.click()} className="w-full rounded-2xl border-2 border-dashed border-[#DEE7E2] bg-white/60 p-8 flex flex-col items-center gap-2 active:scale-[0.98] transition" data-testid="image-pick">
                <Camera size={28} className="text-[#C9A66B]" strokeWidth={1.6} />
                <span className="text-sm font-medium text-[#1A2924]">Vyber nebo vyfoť screenshot</span>
                <span className="text-xs text-[#5F7F77]">WhatsApp · Messenger · SMS · Instagram (max 5 MB)</span>
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="image-input" />
            <p className="text-[0.7rem] text-[#5F7F77] mt-2">AI ze screenshotu přečte text a rovnou ho analyzuje. ✨</p>
          </div>
        ) : (
          <div data-testid="text-mode">
            <label className="label-soft">Konverzace</label>
            <div className="relative">
              <textarea
                data-testid="conv-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={"Vlož celou konverzaci sem.\n\nFormát:\nJá: Ahoj!\nOn: Ahoj, co děláš?\n..."}
                rows={10}
                className="input-field font-mono text-sm leading-relaxed"
              />
              {text.length === 0 && (
                <button type="button" onClick={() => setText(EXAMPLE)} className="absolute bottom-3 right-3 text-xs text-[#C9A66B] underline" data-testid="use-example">
                  Použij ukázku
                </button>
              )}
            </div>
            <p className="text-[0.7rem] text-[#5F7F77] mt-1.5">{text.length} znaků · ideálně 200+ pro nejlepší analýzu</p>
          </div>
        )}

        <div>
          <label className="label-soft">Dodatečný kontext (volitelné)</label>
          <textarea className="input-field text-sm" value={context} onChange={(e) => setContext(e.target.value)} rows={2} placeholder="Např. 'Známe se 3 měsíce, poslední týden mě hodně ignoruje'" data-testid="context" />
        </div>

        {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-2xl p-3" data-testid="analyze-error">{error}</div>}

        <button onClick={submit} disabled={loading} className="btn-primary flex items-center justify-center gap-2" data-testid="analyze-submit">
          {loading ? (
            <><span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> {mode === "image" ? "AI čte a analyzuje..." : "AI analyzuje..."}</>
          ) : (
            <><Sparkles size={18} /> Analyzovat s AI</>
          )}
        </button>
      </div>
    </div>
  );
}
